import { Rating, Skeleton } from "@mui/material";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search as SearchIcon, SlidersHorizontal, ChefHat } from "lucide-react";
import { algolia_index } from "../Algolia/algolia_posts";
import { useAuth } from "../contexts/AuthContext";
import { db } from "../Firebase/firebase";
import "../Media/Posts.css";
import { Button } from "antd";

const CATEGORY_OPTIONS = [
  { label: "GERD-Friendly", value: "GERD-Friendly" },
  { label: "IBS-Friendly", value: "IBS-Friendly" },
  { label: "Gastritis-Friendly", value: "Gastritis-Friendly" },
  { label: "Barrett's Esophagus-Friendly", value: "Barretts-Esophagus-Friendly" },
];

function buildAlgoliaFilter({ categories, calorieRange }) {
  const parts = [];
  if (Array.isArray(categories) && categories.length > 0) {
    const catGroup = categories
      .map((c) => `category:"${c}"`)
      .join(" OR ");
    parts.push(`(${catGroup})`);
  }
  if (calorieRange) {
    parts.push(`calorieTotal:${calorieRange}`);
  }
  return parts.join(" AND ");
}

function parseCategoryParam(raw) {
  if (!raw) return [];
  // Legacy format from older navbar: "A OR category: B OR category: C"
  if (raw.includes("OR category:")) {
    return raw
      .split(/OR category:/i)
      .map((s) => s.trim())
      .filter(Boolean);
  }
  // Comma-separated new format
  return raw.split(",").map((s) => s.trim()).filter(Boolean);
}

export default function SearchPage() {
  const postsRef = collection(db, "posts");
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const search_query = (params.get("search_query") || "").trim();
  const calorieTotalRaw = params.get("calorieTotal") || "";
  const categoryParam = params.get("category") || "";
  const selectedCategories = useMemo(
    () => parseCategoryParam(categoryParam),
    [categoryParam]
  );
  const { logErrorToDatabase } = useAuth();

  // SEO Data - Dynamic based on search query
  const seoData = useMemo(() => {
    const hasQuery = search_query.length > 0;
    const hasCategories = selectedCategories.length > 0;
    const scope = hasCategories
      ? selectedCategories.map((c) => c.replace(/-/g, " ")).join(", ")
      : "digestive-friendly";
    const title = hasQuery
      ? `"${search_query}" ${scope} recipes | GentleGourmet`
      : hasCategories
        ? `${scope} recipes | GentleGourmet`
        : "Search digestive-friendly recipes | GentleGourmet";
    const description = hasQuery
      ? `Find ${scope} recipes matching "${search_query}". Evidence-informed meals for GERD, IBS, gastritis and Barrett's esophagus.`
      : `Browse ${scope} recipes for GERD, acid reflux, IBS and gastritis. Filter by ingredient, calorie range and dietary needs.`;
    return {
      title,
      description,
      canonicalUrl: "https://gentlegourmet.app/search",
    };
  }, [search_query, selectedCategories]);

  const [loading, setLoading] = useState(false);
  const [posts, setPosts] = useState([]);
  const [queryButtonLoading, setQueryButtonLoading] = useState(false);
  const [lastDoc, setLastDoc] = useState();
  const [queryText, setQueryText] = useState(search_query);
  const [activeCategories, setActiveCategories] = useState(selectedCategories);

  useEffect(() => {
    setQueryText(search_query);
    setActiveCategories(selectedCategories);
  }, [search_query, selectedCategories]);

  // Update setPosts state with the new posts.
  const updateState = (collections) => {
    const lastVisible = collections.docs[collections.docs.length - 1];
    collections.forEach((Snapshot) => {
      setPosts((newposts) => [...newposts, Snapshot]);
      setLastDoc(lastVisible);
    });
  };

  const baseQuery = useMemo(
    () =>
      query(
        postsRef,
        orderBy("createdAt", "desc"),
        where("privatePosts", "==", false),
        limit(9)
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  function loadMorePosts() {
    setQueryButtonLoading(true);

    getDocs(baseQuery).then((docSnaps) => {
      if (docSnaps.docs.length === 0 || lastDoc === undefined) {
        setQueryButtonLoading(false);
        return;
      }
      const next = query(
        postsRef,
        orderBy("createdAt", "desc"),
        where("privatePosts", "==", false),
        limit(9),
        startAfter(lastDoc)
      );
      getDocs(next)
        .then((docSnap) => {
          updateState(docSnap);
          setQueryButtonLoading(false);
        })
        .catch((error) => {
          setQueryButtonLoading(false);
          logErrorToDatabase(error, "SearchPage.js");
        });
    });
  }

  useEffect(() => {
    setPosts([]);
    const hasAnyCriteria =
      search_query.length > 0 ||
      selectedCategories.length > 0 ||
      !!calorieTotalRaw;

    // If no search criteria at all, show the most recent public posts instead
    // of returning nothing - much better UX than a blank page.
    if (!hasAnyCriteria) {
      setLoading(true);
      const recent = query(
        postsRef,
        orderBy("createdAt", "desc"),
        where("privatePosts", "==", false),
        limit(12)
      );
      getDocs(recent)
        .then((snap) => {
          const arr = [];
          snap.forEach((d) => arr.push(d));
          setPosts(arr);
          setLastDoc(snap.docs[snap.docs.length - 1]);
        })
        .catch((err) => console.log(err))
        .finally(() => setLoading(false));
      return;
    }

    setLoading(true);
    const filters = buildAlgoliaFilter({
      categories: selectedCategories,
      calorieRange: calorieTotalRaw,
    });

    try {
      algolia_index
        .search(search_query, {
          attributesToRetrieve: ["path", "objectID"],
          ...(filters ? { filters } : {}),
          hitsPerPage: 30,
        })
        .then(async (result) => {
          const hits = result?.hits || [];
          if (hits.length === 0) {
            setLoading(false);
            return;
          }
          const docs = await Promise.all(
            hits.map((hit) =>
              getDoc(doc(db, "posts", hit.objectID)).catch(() => null)
            )
          );
          const visible = docs.filter(
            (snap) => snap && snap.exists() && snap.data().privatePosts !== true
          );
          setPosts(visible);
          setLoading(false);
        })
        .catch(() => setLoading(false));
    } catch (err) {
      console.log(err);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search_query, calorieTotalRaw, categoryParam]);

  const toggleCategory = useCallback((cat) => {
    setActiveCategories((prev) =>
      prev.includes(cat) ? prev.filter((c) => c !== cat) : [...prev, cat]
    );
  }, []);

  const runSearch = useCallback(
    (e) => {
      if (e) e.preventDefault();
      const sp = new URLSearchParams();
      if (queryText.trim()) sp.set("search_query", queryText.trim());
      if (activeCategories.length > 0)
        sp.set("category", activeCategories.join(","));
      sp.set("calorieTotal", "0 TO 50000");
      navigate(`/search?${sp.toString()}`);
    },
    [queryText, activeCategories, navigate]
  );

  function manipulateDescription(postDesc) {
    const desc = postDesc.data().desc || "";
    return desc.length >= 140 ? desc.substr(0, 140) + "…" : desc;
  }

  const resultCountLabel = loading
    ? "Searching…"
    : `${posts.length} ${posts.length === 1 ? "recipe" : "recipes"}`;

  const activeChips = [
    ...(search_query ? [{ label: `"${search_query}"`, key: "q" }] : []),
    ...selectedCategories.map((c) => ({ label: c.replace(/-/g, " "), key: c })),
  ];

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <link rel="canonical" href={seoData.canonicalUrl} />
        <meta name="robots" content="noindex, follow" />
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:url" content={seoData.canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="GentleGourmet" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
      </Helmet>

      <main className="min-h-screen bg-stone-50">
        {/* Search header */}
        <section className="border-b border-stone-200 bg-white">
          <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
            <nav aria-label="Breadcrumb" className="mb-3 text-sm text-stone-500">
              <ol className="flex items-center gap-2">
                <li>
                  <Link to="/" className="hover:text-stone-900">
                    Home
                  </Link>
                </li>
                <li aria-hidden>/</li>
                <li className="text-stone-900">Search</li>
              </ol>
            </nav>

            <h1 className="text-3xl sm:text-4xl font-serif text-stone-900 tracking-tight">
              {search_query
                ? `Results for "${search_query}"`
                : selectedCategories.length > 0
                  ? `${selectedCategories[0].replace(/-/g, " ")} recipes`
                  : "Search digestive-friendly recipes"}
            </h1>
            <p className="mt-2 text-stone-600 max-w-2xl">
              Explore GERD, acid reflux, IBS, gastritis and Barrett's
              esophagus-friendly dishes. Search by keyword or filter by
              condition &mdash; both optional.
            </p>

            <form onSubmit={runSearch} className="mt-6" role="search">
              <div className="flex items-stretch gap-2 max-w-2xl">
                <label htmlFor="search-input" className="sr-only">
                  Search recipes
                </label>
                <div className="relative flex-1">
                  <SearchIcon
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"
                    size={18}
                    aria-hidden
                  />
                  <input
                    id="search-input"
                    type="search"
                    value={queryText}
                    onChange={(e) => setQueryText(e.target.value)}
                    placeholder="Try oatmeal, salmon, low-acid..."
                    className="w-full h-12 pl-10 pr-4 rounded-md border border-stone-300 bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-emerald-700 focus:border-emerald-700"
                  />
                </div>
                <button
                  type="submit"
                  className="h-12 px-6 rounded-md bg-emerald-800 text-white font-medium hover:bg-emerald-900 transition-colors"
                >
                  Search
                </button>
              </div>

              <fieldset className="mt-5">
                <legend className="flex items-center gap-2 text-sm font-medium text-stone-700 mb-2">
                  <SlidersHorizontal size={14} aria-hidden /> Filter by condition
                  <span className="text-stone-400 font-normal">(optional)</span>
                </legend>
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_OPTIONS.map((opt) => {
                    const checked = activeCategories.includes(opt.value);
                    return (
                      <label
                        key={opt.value}
                        className={[
                          "cursor-pointer select-none rounded-full border px-4 py-1.5 text-sm transition-colors",
                          checked
                            ? "bg-stone-900 text-white border-stone-900"
                            : "bg-white text-stone-700 border-stone-300 hover:border-stone-500",
                        ].join(" ")}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => toggleCategory(opt.value)}
                        />
                        {opt.label}
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            </form>
          </div>
        </section>

        {/* Results */}
        <section className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-stone-600" aria-live="polite">
              {resultCountLabel}
            </p>
            {activeChips.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-end">
                {activeChips.map((c) => (
                  <span
                    key={c.key}
                    className="inline-flex items-center rounded-full bg-stone-100 border border-stone-200 px-3 py-1 text-xs text-stone-700"
                  >
                    {c.label}
                  </span>
                ))}
              </div>
            )}
          </div>

          {!loading && posts.length === 0 ? (
            <div className="text-center py-20 border border-dashed border-stone-300 rounded-lg bg-white">
              <ChefHat className="mx-auto text-stone-400" size={40} aria-hidden />
              <h2 className="mt-4 text-xl font-serif text-stone-900">
                No matching recipes
              </h2>
              <p className="mt-2 text-stone-600 max-w-md mx-auto">
                Try removing filters, broadening your keyword, or browse our
                curated digestive-friendly collections from the home page.
              </p>
              <Link
                to="/"
                reloadDocument
                className="inline-block mt-5 text-emerald-800 underline underline-offset-4 hover:text-emerald-900"
              >
                Back to recipes
              </Link>
            </div>
          ) : (
            <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {loading && posts.length === 0
                ? [...Array(6)].map((_, i) => (
                    <li
                      key={i}
                      className="bg-white border border-stone-200 rounded-lg overflow-hidden"
                    >
                      <Skeleton variant="rectangular" height={220} />
                      <div className="p-4">
                        <Skeleton variant="text" height={28} width="80%" />
                        <Skeleton variant="text" height={20} width="60%" />
                        <Skeleton variant="text" height={20} width="40%" />
                      </div>
                    </li>
                  ))
                : posts.map((post) => (
                    <li
                      key={post.id}
                      className="group bg-white border border-stone-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow"
                    >
                      <Link
                        reloadDocument
                        to={`/post/${post.data().category}/${post.id}`}
                        className="block"
                      >
                        <div className="aspect-[4/3] overflow-hidden bg-stone-100">
                          <img
                            src={
                              post.data().imageUrl ||
                              "https://firebasestorage.googleapis.com/v0/b/gingerrmarket.appspot.com/o/defaultImages%2Fphoto-1546069901-ba9599a7e63c.jpg?alt=media&token=c55c094f-91f0-4d7a-85d4-8ae2d125cce2"
                            }
                            alt={post.data().title}
                            loading="lazy"
                            className="h-full w-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
                          />
                        </div>
                      </Link>

                      <div className="p-5">
                        <div className="flex items-center gap-2 text-xs text-stone-500 mb-2">
                          <Link
                            reloadDocument
                            to={`/explore/${post.data().category}/all`}
                            className="uppercase tracking-wider font-medium text-emerald-800 hover:text-emerald-900"
                          >
                            {(post.data().category || "").replace(/-/g, " ")}
                          </Link>
                          <span aria-hidden>·</span>
                          <span>{post.data().calorieTotal || 0} cal</span>
                        </div>

                        <h3 className="text-lg font-serif text-stone-900 leading-snug">
                          <Link
                            reloadDocument
                            to={`/post/${post.data().category}/${post.id}`}
                            className="hover:underline"
                          >
                            {post.data().title}
                          </Link>
                        </h3>

                        {post.data().desc && (
                          <p className="mt-2 text-sm text-stone-600 line-clamp-2">
                            {manipulateDescription(post)}
                          </p>
                        )}

                        <div className="mt-3 flex items-center justify-between">
                          <Rating
                            name="read-only"
                            value={post?.data().rating || 0}
                            size="small"
                            readOnly
                          />
                          <Link
                            reloadDocument
                            to={`/profile/${post.data().username_of_user}`}
                            className="text-xs text-stone-500 hover:text-stone-700"
                          >
                            by {post.data().username_of_user || "anonymous"}
                          </Link>
                        </div>
                      </div>
                    </li>
                  ))}
            </ul>
          )}

          {posts.length > 0 && (
            <div className="mt-10 flex justify-center">
              <Button
                type="default"
                loading={queryButtonLoading}
                onClick={() => loadMorePosts()}
              >
                Load more
              </Button>
            </div>
          )}
        </section>
      </main>
    </>
  );
}
